import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  TextField,
  Button,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Divider,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import axios from "axios";

const AddEmployee = () => {
  const { accessToken, userDetails } = useAuth();
  const [email, setEmail] = useState("");
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [agencies, setAgencies] = useState([]);
  const [selectedAgencies, setSelectedAgencies] = useState([]);

  const fetchAgencies = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/dash/pro-agencies/list`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.status === 200 && response.data && response.data.success) {
        setAgencies(response.data.agencies || []);
      } else {
        setMessage(response.data.message || "Failed to fetch the agencies.");
      }
    } catch (error) {
      setMessage("Error while fetching the agencies");
    } finally {
      setLoading(false);
    }
  };

  const handleAgencySelection = (agencyId) => {
    setSelectedAgencies((prevSelected) =>
      prevSelected.includes(agencyId)
        ? prevSelected.filter((id) => id !== agencyId)
        : [...prevSelected, agencyId]
    );
  };

  const searchEmployee = async () => {
    setLoading(true);
    setMessage("");
    try {
      if (email === userDetails.email) {
        setMessage("You cannot add yourself as an employee.");
        setLoading(false);
        return;
      }
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/user/search-employee`,
        {
          params: { email },
        }
      );
      if (response.data) {
        setEmployee(response.data.data);
        fetchAgencies();
      } else {
        setMessage("Employee not found.");
      }
    } catch (error) {
      setMessage(
        error.response?.data?.error || "Error searching for employee."
      );
    } finally {
      setLoading(false);
    }
  };

  const addEmployee = async () => {
    setLoading(true);
    setMessage("");
    try {
      await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/dash/pro-agencies/add-employee`,
        {
          agencyIds: selectedAgencies,
          proId: employee.pro_id,
        }
      );
      setMessage("Employee added successfully.");
      setEmployee(null);
      setEmail("");
      setSelectedAgencies([]);
    } catch (error) {
      setMessage(error.response?.data?.error || "Error adding employee.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Typography variant="h6" gutterBottom>
        Add Employee
      </Typography>
      <TextField
        label="Enter Employee Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        fullWidth
        margin="normal"
      />
      <Button
        variant="contained"
        color="primary"
        onClick={searchEmployee}
        disabled={loading}
        sx={{ mb: 2 }}
      >
        Search Employee
      </Button>
      {loading && <CircularProgress size={24} />}
      {message && (
        <Typography
          color={message.includes("successfully") ? "green" : "error"}
        >
          {message}
        </Typography>
      )}
      {employee && (
        <Card variant="outlined" sx={{ mt: 2, p: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Employee Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography>
              <strong>Name:</strong> {employee.first_name} {employee.last_name}
            </Typography>
            <Typography>
              <strong>Email:</strong> {employee.email}
            </Typography>
            <Typography variant="h6" sx={{ mt: 2 }}>
              Select Agencies
            </Typography>
            <FormGroup>
              {agencies.map((agency) => (
                <FormControlLabel
                  key={agency.id}
                  control={
                    <Checkbox
                      checked={selectedAgencies.includes(agency.id)}
                      onChange={() => handleAgencySelection(agency.id)}
                    />
                  }
                  label={agency.agency_name}
                />
              ))}
            </FormGroup>
          </CardContent>
          <CardActions>
            <Button
              variant="contained"
              color="secondary"
              onClick={addEmployee}
              disabled={loading || selectedAgencies.length === 0}
            >
              Add Employee
            </Button>
          </CardActions>
        </Card>
      )}
    </div>
  );
};

export default AddEmployee;
